import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Save, FileText, Building2, MessageSquare, Loader2, Briefcase, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminProposalSettings = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [company, setCompany] = useState({
    company_name: "Evolutt",
    company_slogan: "Unlocking What's Next",
    company_address: "",
    company_postal_code: "",
    company_city: "Portugal",
    company_phone1: "",
    company_phone2: "",
    company_website: "",
    company_email: "",
  });

  // Textos comuns (capa + agradecimento + rodapé)
  const [common, setCommon] = useState({
    cover_title: "Proposta Financeira",
    cover_subtitle: "",
    greeting_template: "Olá {primeiro_nome},",
    thanks_title: "Obrigado!",
    thanks_text: "Estamos disponíveis para esclarecer qualquer dúvida. Aguardamos a tua resposta com entusiasmo!",
    footer_text: "© {ano} Evolutt - Unlocking What's Next",
  });

  // Textos específicos do Contrato
  const [contractTexts, setContractTexts] = useState({
    motivational_text: "Muitos parabéns! Estamos entusiasmados com a possibilidade de contarmos contigo na nossa equipa. Acreditamos que o teu talento e dedicação serão uma mais-valia para os nossos projetos. Preparámos esta proposta financeira especialmente para ti.",
    annual_title: "Visão Anual Detalhada",
    annual_subtitle: "Projeção de 12 meses com todos os benefícios e descontos",
    other_benefits_title: "Benefícios",
    other_benefits_text: "",
  });

  // Textos específicos Recibos Verdes
  const [recibosTexts, setRecibosTexts] = useState({
    motivational_text: "Muitos parabéns! Estamos entusiasmados com a possibilidade de contarmos contigo na nossa equipa.",
    other_benefits_title: "Benefícios",
    other_benefits_text_freelancer: "",
    freelancer_payment_months: 11,
  });

  // Textos específicos B2B
  const [b2bTexts, setB2bTexts] = useState({
    motivational_text: "Muitos parabéns! Estamos entusiasmados com a possibilidade de contarmos contigo na nossa equipa.",
    other_benefits_title: "Benefícios",
    other_benefits_text_b2b: "",
    b2b_payment_months: 11,
  });

  useEffect(() => { loadDefaults(); }, []);

  const loadDefaults = async () => {
    try {
      const res = await axios.get(`${API}/proposal-settings/defaults`, { headers: getAuthHeaders() });
      if (res.data?.company) setCompany(prev => ({ ...prev, ...res.data.company }));
      if (res.data?.texts) {
        const t = res.data.texts;
        setCommon(prev => ({
          ...prev,
          cover_title: t.cover_title ?? prev.cover_title,
          cover_subtitle: t.cover_subtitle ?? prev.cover_subtitle,
          greeting_template: t.greeting_template ?? prev.greeting_template,
          thanks_title: t.thanks_title ?? prev.thanks_title,
          thanks_text: t.thanks_text ?? prev.thanks_text,
          footer_text: t.footer_text ?? prev.footer_text,
        }));
        setContractTexts(prev => ({
          ...prev,
          motivational_text: t.motivational_text ?? prev.motivational_text,
          annual_title: t.annual_title ?? prev.annual_title,
          annual_subtitle: t.annual_subtitle ?? prev.annual_subtitle,
          other_benefits_title: t.other_benefits_title ?? prev.other_benefits_title,
          other_benefits_text: t.other_benefits_text ?? prev.other_benefits_text,
        }));
        setRecibosTexts(prev => ({
          ...prev,
          motivational_text: t.motivational_text_freelancer ?? prev.motivational_text,
          other_benefits_title: t.other_benefits_title_freelancer ?? prev.other_benefits_title,
          other_benefits_text_freelancer: t.other_benefits_text_freelancer ?? prev.other_benefits_text_freelancer,
          freelancer_payment_months: t.freelancer_payment_months ?? prev.freelancer_payment_months,
        }));
        setB2bTexts(prev => ({
          ...prev,
          motivational_text: t.motivational_text_b2b ?? prev.motivational_text,
          other_benefits_title: t.other_benefits_title_b2b ?? prev.other_benefits_title,
          other_benefits_text_b2b: t.other_benefits_text_b2b ?? prev.other_benefits_text_b2b,
          b2b_payment_months: t.b2b_payment_months ?? prev.b2b_payment_months,
        }));
      }
    } catch (err) {
      console.error("Error loading defaults:", err);
    } finally {
      setLoading(false);
    }
  };

  const saveDefaults = async () => {
    setSaving(true);
    try {
      const texts = {
        // Comuns
        ...common,
        // Contrato
        motivational_text: contractTexts.motivational_text,
        annual_title: contractTexts.annual_title,
        annual_subtitle: contractTexts.annual_subtitle,
        other_benefits_title: contractTexts.other_benefits_title,
        other_benefits_text: contractTexts.other_benefits_text,
        // Recibos Verdes
        motivational_text_freelancer: recibosTexts.motivational_text,
        other_benefits_title_freelancer: recibosTexts.other_benefits_title,
        other_benefits_text_freelancer: recibosTexts.other_benefits_text_freelancer,
        freelancer_payment_months: parseInt(recibosTexts.freelancer_payment_months || 11),
        // B2B
        motivational_text_b2b: b2bTexts.motivational_text,
        other_benefits_title_b2b: b2bTexts.other_benefits_title,
        other_benefits_text_b2b: b2bTexts.other_benefits_text_b2b,
        b2b_payment_months: parseInt(b2bTexts.b2b_payment_months || 11),
      };
      await axios.post(`${API}/proposal-settings/defaults`, { company, texts }, { headers: getAuthHeaders() });
      toast.success("Definições padrão guardadas com sucesso");
    } catch (err) {
      toast.error("Erro ao guardar definições");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-proposal-settings">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button>
            </Link>
            <FileText className="w-5 h-5 text-[var(--brand-primary)]" />
            <span className="font-manrope font-bold text-lg">Definições da Proposta PDF</span>
          </div>
          <Button onClick={saveDefaults} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)] text-white" data-testid="save-defaults-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar Padrão
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <p className="text-sm text-muted-foreground">
          Estas definições servem como padrão para todos os utilizadores. Cada utilizador pode personalizar na altura da exportação.
        </p>

        {/* Company Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="w-4 h-4 text-[var(--brand-primary)]" />
              Dados da Empresa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome da Empresa</Label>
                <Input value={company.company_name} onChange={e => setCompany({...company, company_name: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Slogan</Label>
                <Input value={company.company_slogan} onChange={e => setCompany({...company, company_slogan: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Morada</Label>
              <Input value={company.company_address} onChange={e => setCompany({...company, company_address: e.target.value})} className="mt-1" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Código Postal</Label>
                <Input value={company.company_postal_code} onChange={e => setCompany({...company, company_postal_code: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Cidade</Label>
                <Input value={company.company_city} onChange={e => setCompany({...company, company_city: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Website</Label>
                <Input value={company.company_website} onChange={e => setCompany({...company, company_website: e.target.value})} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Telefone 1</Label>
                <Input value={company.company_phone1} onChange={e => setCompany({...company, company_phone1: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Telefone 2</Label>
                <Input value={company.company_phone2} onChange={e => setCompany({...company, company_phone2: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={company.company_email} onChange={e => setCompany({...company, company_email: e.target.value})} className="mt-1" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Textos Comuns */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="w-4 h-4 text-[var(--brand-primary)]" />
              Textos Comuns (Capa + Agradecimento + Rodapé)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Título da Capa</Label>
                <Input value={common.cover_title} onChange={e => setCommon({...common, cover_title: e.target.value})} className="mt-1" />
              </div>
              <div>
                <Label>Subtítulo da Capa</Label>
                <Input value={common.cover_subtitle} onChange={e => setCommon({...common, cover_subtitle: e.target.value})} className="mt-1" placeholder="Opcional" />
              </div>
            </div>
            <div>
              <Label>Saudação (use {"{primeiro_nome}"} para o nome do consultor)</Label>
              <Input value={common.greeting_template} onChange={e => setCommon({...common, greeting_template: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>Título Agradecimento</Label>
              <Input value={common.thanks_title} onChange={e => setCommon({...common, thanks_title: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>Texto Agradecimento</Label>
              <Textarea value={common.thanks_text} onChange={e => setCommon({...common, thanks_text: e.target.value})} className="mt-1 min-h-[80px]" />
            </div>
            <div>
              <Label>Rodapé (use {"{ano}"} para o ano atual)</Label>
              <Input value={common.footer_text} onChange={e => setCommon({...common, footer_text: e.target.value})} className="mt-1" />
            </div>
          </CardContent>
        </Card>

        {/* Tabs Contrato vs Recibos/B2B */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4 text-[var(--brand-primary)]" />
              Textos Específicos por Regime
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="contract" className="w-full" data-testid="regime-tabs">
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="contract" className="flex items-center gap-2" data-testid="tab-contract">
                  <Briefcase className="w-4 h-4" />
                  Contrato
                </TabsTrigger>
                <TabsTrigger value="recibos" className="flex items-center gap-2" data-testid="tab-recibos">
                  <Users className="w-4 h-4" />
                  Recibos Verdes
                </TabsTrigger>
                <TabsTrigger value="b2b" className="flex items-center gap-2" data-testid="tab-b2b">
                  <Users className="w-4 h-4" />
                  B2B
                </TabsTrigger>
              </TabsList>

              {/* CONTRATO */}
              <TabsContent value="contract" className="space-y-4 mt-4">
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-xs text-blue-800">
                    Contrato inclui <strong>Visão Anual</strong> (12 meses) na proposta PDF.
                  </p>
                </div>
                <div>
                  <Label>Texto Motivacional</Label>
                  <Textarea value={contractTexts.motivational_text} onChange={e => setContractTexts({...contractTexts, motivational_text: e.target.value})} className="mt-1 min-h-[100px]" data-testid="contract-motivational" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Título Visão Anual</Label>
                    <Input value={contractTexts.annual_title} onChange={e => setContractTexts({...contractTexts, annual_title: e.target.value})} className="mt-1" data-testid="contract-annual-title" />
                  </div>
                  <div>
                    <Label>Subtítulo Anual</Label>
                    <Input value={contractTexts.annual_subtitle} onChange={e => setContractTexts({...contractTexts, annual_subtitle: e.target.value})} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label>Título Benefícios</Label>
                    <Input
                      value={contractTexts.other_benefits_title}
                      onChange={e => setContractTexts({...contractTexts, other_benefits_title: e.target.value})}
                      className="mt-1"
                      data-testid="contract-other-benefits-title"
                      placeholder="Benefícios"
                    />
                  </div>
                  <div>
                    <Label>Texto Benefícios (Contrato)</Label>
                    <Textarea
                      value={contractTexts.other_benefits_text}
                      onChange={e => setContractTexts({...contractTexts, other_benefits_text: e.target.value})}
                      className="mt-1 min-h-[200px] text-xs"
                      data-testid="contract-other-benefits-text"
                      placeholder="Use - para bullets, **negrito** entre asteriscos"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Será usado em propostas de regime <strong>Contrato</strong>.
                    </p>
                  </div>
                </div>
              </TabsContent>

              {/* RECIBOS VERDES */}
              <TabsContent value="recibos" className="space-y-4 mt-4">
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-xs text-amber-800">
                    Recibos Verdes <strong>não tem Visão Anual</strong>. A tabela mostra Valor Bruto Mensal e Valor Líquido Mensal.
                  </p>
                </div>
                <div>
                  <Label>Texto Motivacional</Label>
                  <Textarea value={recibosTexts.motivational_text} onChange={e => setRecibosTexts({...recibosTexts, motivational_text: e.target.value})} className="mt-1 min-h-[100px]" data-testid="recibos-motivational" />
                </div>
                <div className="flex items-center gap-2">
                  <Label>Meses de Faturação (padrão)</Label>
                  <div className="inline-flex rounded-md border border-[var(--brand-primary)]/20 overflow-hidden" data-testid="default-recibos-months">
                    {[11, 12].map(m => (
                      <button type="button" key={m}
                        onClick={() => setRecibosTexts({ ...recibosTexts, freelancer_payment_months: m })}
                        className={`px-3 py-0.5 text-[11px] font-medium transition-colors ${parseInt(recibosTexts.freelancer_payment_months) === m ? "bg-[var(--brand-primary)] text-white" : "bg-white text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10"}`}
                        data-testid={`default-recibos-months-${m}`}>
                        {m} meses
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label>Título Benefícios</Label>
                    <Input value={recibosTexts.other_benefits_title} onChange={e => setRecibosTexts({...recibosTexts, other_benefits_title: e.target.value})} className="mt-1" data-testid="recibos-other-benefits-title" placeholder="Benefícios" />
                  </div>
                  <div>
                    <Label>Texto Benefícios (Recibos Verdes)</Label>
                    <Textarea value={recibosTexts.other_benefits_text_freelancer} onChange={e => setRecibosTexts({...recibosTexts, other_benefits_text_freelancer: e.target.value})} className="mt-1 min-h-[200px] text-xs" data-testid="recibos-other-benefits-text" placeholder="Use - para bullets, **negrito** entre asteriscos" />
                    <p className="text-[10px] text-muted-foreground mt-1">Será usado em propostas de regime <strong>Recibos Verdes</strong>.</p>
                  </div>
                </div>
              </TabsContent>

              {/* B2B */}
              <TabsContent value="b2b" className="space-y-4 mt-4">
                <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
                  <p className="text-xs text-purple-800">
                    B2B <strong>não tem Visão Anual</strong> nem Líquido Mensal (não há retenção). A tabela mostra apenas Valor Bruto Mensal.
                  </p>
                </div>
                <div>
                  <Label>Texto Motivacional</Label>
                  <Textarea value={b2bTexts.motivational_text} onChange={e => setB2bTexts({...b2bTexts, motivational_text: e.target.value})} className="mt-1 min-h-[100px]" data-testid="b2b-motivational" />
                </div>
                <div className="flex items-center gap-2">
                  <Label>Meses de Faturação (padrão)</Label>
                  <div className="inline-flex rounded-md border border-[var(--brand-primary)]/20 overflow-hidden" data-testid="default-b2b-months">
                    {[11, 12].map(m => (
                      <button type="button" key={m}
                        onClick={() => setB2bTexts({ ...b2bTexts, b2b_payment_months: m })}
                        className={`px-3 py-0.5 text-[11px] font-medium transition-colors ${parseInt(b2bTexts.b2b_payment_months) === m ? "bg-[var(--brand-primary)] text-white" : "bg-white text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10"}`}
                        data-testid={`default-b2b-months-${m}`}>
                        {m} meses
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label>Título Benefícios</Label>
                    <Input value={b2bTexts.other_benefits_title} onChange={e => setB2bTexts({...b2bTexts, other_benefits_title: e.target.value})} className="mt-1" data-testid="b2b-other-benefits-title" placeholder="Benefícios" />
                  </div>
                  <div>
                    <Label>Texto Benefícios (B2B)</Label>
                    <Textarea value={b2bTexts.other_benefits_text_b2b} onChange={e => setB2bTexts({...b2bTexts, other_benefits_text_b2b: e.target.value})} className="mt-1 min-h-[200px] text-xs" data-testid="b2b-other-benefits-text" placeholder="Use - para bullets, **negrito** entre asteriscos" />
                    <p className="text-[10px] text-muted-foreground mt-1">Será usado em propostas de regime <strong>B2B</strong>.</p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminProposalSettings;
